import { MeasureController } from "../../MeasureController";
import { JudgmentResult, JudgmentResultEnum } from "../../types.client";
import { ManholeToxicGasMeasure } from "../../../models/schemas/measures/misc.schema";
import { parseNumericValue } from "../../utils/parseValue.utils";
import { MANHOLE_TOXIC_GAS_THRESHOLDS } from "../../constants/judgmentCriteria";

/**
 * 맨홀 유해가스 컨트롤러
 * parseNumericValue() 사용 필수: "18.5%" → 18.5, "5ppm" → 5
 * 판정 기준 (MANHOLE_TOXIC_GAS_THRESHOLDS 사용, 모두 충족해야 GOOD):
 * - 산소(O2): o2.min~o2.max (18~23.5%)
 * - 황화수소(H2S): h2s.max 미만 (10ppm)
 * - 일산화탄소(CO): co.max 미만 (30ppm)
 */
export class ManholeToxicGasController extends MeasureController<ManholeToxicGasMeasure> {
  protected getDefaultData(): ManholeToxicGasMeasure {
    return {
      oxygen: "",
      oxygenResult: "",
      hydrogenSulfide: "",
      hydrogenSulfideResult: "",
      carbonMonoxide: "",
      carbonMonoxideResult: "",
      unit: "%/ppm",
      photo: { checkUrl: "", resultUrl: "" },
      result: null,
      discussion: "",
      remark: "",
      createdAt: null,
    };
  }

  protected judge(): JudgmentResult {
    // 각 가스 값 파싱 (parseNumericValue 사용)
    const o2 = parseNumericValue(this.data.oxygen);
    const h2s = parseNumericValue(this.data.hydrogenSulfide);
    const co = parseNumericValue(this.data.carbonMonoxide);

    // 개별 가스 결과 자동 판정 (MANHOLE_TOXIC_GAS_THRESHOLDS 사용)
    const { o2: o2Threshold, h2s: h2sThreshold, co: coThreshold } = MANHOLE_TOXIC_GAS_THRESHOLDS;
    // O2: o2.min~o2.max 범위 내 "양호", 범위 외 "불량"
    if (o2 !== null) {
      this.data.oxygenResult = o2 >= o2Threshold.min && o2 <= o2Threshold.max ? "양호" : "불량";
    }
    // H2S: h2s.max 미만 "양호", 이상 "불량"
    if (h2s !== null) {
      this.data.hydrogenSulfideResult = h2s < h2sThreshold.max ? "양호" : "불량";
    }
    // CO: co.max 미만 "양호", 이상 "불량"
    if (co !== null) {
      this.data.carbonMonoxideResult = co < coThreshold.max ? "양호" : "불량";
    }

    // 모든 값이 null이면 NA
    if (o2 === null && h2s === null && co === null) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    // 각 가스별 판정
    const o2Ok = o2 !== null && o2 >= o2Threshold.min && o2 <= o2Threshold.max;
    const h2sOk = h2s !== null && h2s < h2sThreshold.max;
    const coOk = co !== null && co < coThreshold.max;

    // 측정된 값들이 모두 기준 내이면 GOOD
    const measuredGases = [
      o2 !== null ? o2Ok : null,
      h2s !== null ? h2sOk : null,
      co !== null ? coOk : null,
    ].filter((v) => v !== null);

    if (measuredGases.length > 0 && measuredGases.every((v) => v === true)) {
      return {
        result: JudgmentResultEnum.GOOD,
        discussion: this.generateDiscussion(JudgmentResultEnum.GOOD),
      };
    }

    // 하나라도 기준 밖이면 BAD
    return {
      result: JudgmentResultEnum.BAD,
      discussion: this.generateDiscussion(JudgmentResultEnum.BAD),
    };
  }

  protected generateDiscussion(result: "GOOD" | "CAUTION" | "BAD" | "NA"): string {
    const o2 = parseNumericValue(this.data.oxygen);
    const h2s = parseNumericValue(this.data.hydrogenSulfide);
    const co = parseNumericValue(this.data.carbonMonoxide);
    const { o2: o2Threshold, h2s: h2sThreshold, co: coThreshold } = MANHOLE_TOXIC_GAS_THRESHOLDS;

    if (result === JudgmentResultEnum.NA) {
      return "유해가스 측정값이 입력되지 않았습니다.";
    }

    const parts: string[] = [];
    const issues: string[] = [];

    // 산소 (o2.min~o2.max)
    if (o2 !== null) {
      const o2Ok = o2 >= o2Threshold.min && o2 <= o2Threshold.max;
      parts.push(`O2: ${o2}%`);
      if (!o2Ok) {
        issues.push(`산소(${o2}%)는 기준(${o2Threshold.min}~${o2Threshold.max}%) 범위 밖`);
      }
    }

    // 황화수소 (<h2s.max)
    if (h2s !== null) {
      const h2sOk = h2s < h2sThreshold.max;
      parts.push(`H2S: ${h2s}ppm`);
      if (!h2sOk) {
        issues.push(`황화수소(${h2s}ppm)는 기준(${h2sThreshold.max}ppm 미만) 초과`);
      }
    }

    // 일산화탄소 (<co.max)
    if (co !== null) {
      const coOk = co < coThreshold.max;
      parts.push(`CO: ${co}ppm`);
      if (!coOk) {
        issues.push(`일산화탄소(${co}ppm)는 기준(${coThreshold.max}ppm 미만) 초과`);
      }
    }

    if (result === JudgmentResultEnum.GOOD) {
      return `${parts.join(", ")}로 모두 정상 범위입니다.`;
    }

    return `${parts.join(", ")} - ${issues.join(", ")}입니다.`;
  }
}
